import { cn } from "@/util/cn";

import applePayLogo from "@/res/logos/Apple-Pay-Mark/apple-pay-logo.png";
import googlePayLogo from "@/res/logos/Google-Pay-Mark/google-pay-logo.png";
import paypalLogo from "@/res/logos/PayPal_Logo_2022/PayPal_Logo_Horizontal_Full_Color_RGB.png";
import payIn4Logo from "@/res/logos/PayPal_Payin4.png";
import creditCardLogo from "@/res/logos/visa-logo.png";
import masterCardLogo from "@/res/logos/mastercard-logo.png";
import amexLogo from "@/res/logos/amex-logo.png";
import paytoLogo from "@/res/logos/payto-logo.svg";
import klarnaLogo from "@/res/logos/klarna-logo.svg";
import afterpayLogo from "@/res/logos/afterpay-logo.png";
// import creditCardLogo from "@/res/logos/mint/mint.webp";
import { ReactNode } from "react";
import { PaymentType } from "@/queries/payments/useCreatePayment";

export const PaymentMethods = [
  {
    name: "Apple Pay",
    value: PaymentType.Apple,
    icon: applePayLogo,
    description: "Pay with Apple Pay",
  },
  {
    name: "Google Pay",
    value: PaymentType.Google,
    icon: googlePayLogo,
    description: "Pay with Google Pay",
  },
  {
    name: "PayPal",
    value: PaymentType.PayPal,
    icon: paypalLogo,
    description: "Pay securely with your PayPal account",
  },
  {
    name: "Pay in 4",
    value: PaymentType.PayPalPayIn4,
    icon: payIn4Logo,
    description: "Split your payment into 4 with PayPal",
  },
  {
    name: "Afterpay",
    value: PaymentType.Afterpay,
    icon: afterpayLogo,
    description: "Pay in 4 installments with Afterpay",
  },
  {
    name: "Klarna",
    value: PaymentType.Klarna,
    icon: klarnaLogo,
    description: "Buy now, pay later with Klarna",
  },
  {
    name: "PayTo",
    value: PaymentType.PayTo,
    icon: paytoLogo,
    description: "Instant bank transfer via PayTo",
  },
  {
    name: "Credit Card",
    value: PaymentType.Mint,
    icon: creditCardLogo,
    description: "Secure credit card payment",
  },
];

interface PaymentMethodProps {
  selectedMethod: PaymentType | null;
  onMethodSelect: (method: PaymentType | null) => void;
  renderSelectedContent?: (method: PaymentType) => ReactNode;
  disabled?: boolean;
}

export default function PaymentMethod({
  selectedMethod,
  onMethodSelect,
  availableMethods,
  renderSelectedContent,
  disabled = false,
}: PaymentMethodProps & { availableMethods?: PaymentType[] }) {
  const methods = PaymentMethods.filter((m) =>
    availableMethods ? availableMethods.includes(m.value) : true
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 items-stretch">
        {methods.map((method) => (
          <MethodComponent
            key={method.value}
            method={method}
            selectedMethod={selectedMethod}
            onMethodSelect={onMethodSelect}
            disabled={disabled}
            selectedContent={
              selectedMethod === method.value && renderSelectedContent
                ? renderSelectedContent(method.value)
                : null
            }
          />
        ))}
      </div>
    </div>
  );
}

type MethodComponentProps = {
  method: {
    name: string;
    value: PaymentType;
    icon: string;
    description?: string;
  };
  selectedMethod: PaymentType | null;
  onMethodSelect: (method: PaymentType | null) => void;
  selectedContent?: ReactNode;
  disabled: boolean;
};

function MethodComponent({
  method,
  selectedMethod,
  onMethodSelect,
  selectedContent,
  disabled,
}: MethodComponentProps) {
  const isSelected = selectedMethod === method.value;
  return (
    <div className="space-y-3">
      <label
        key={method.value}
        aria-disabled={disabled}
        className={cn(
          "flex flex-col items-center sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 border rounded-lg transition-all min-h-[5rem]",
          disabled
            ? "pointer-events-none cursor-not-allowed opacity-60"
            : "cursor-pointer",
          isSelected
            ? "border-brand-blue-300 bg-brand-blue-300/10 text-brand-blue-300"
            : disabled
              ? "border-brand-neutral-600"
              : "hover:border-white/80"
        )}
      >
        <div className="flex items-center gap-3 justify-center sm:justify-start">
          <input
            type="radio"
            name="paymentMethod"
            value={method.value}
            checked={isSelected}
            onChange={() => {
              if (!disabled) {
                onMethodSelect(method.value);
              }
            }}
            disabled={disabled}
            aria-label={
              method.value === PaymentType.Mint
                ? "Credit card (Visa, Mastercard, American Express)"
                : method.name
            }
            className="sr-only peer"
          />
          <div
            className={cn(
              "h-4 w-4 rounded-full border transition-all",
              isSelected ? "border-brand-blue-300 border-4" : "border-brand-neutral-500"
            )}
          />
        </div>

        <div
          className={
            method.value === PaymentType.Mint
              ? "py-2 px-2"
              : "py-1 px-1 bg-white/80 rounded-lg w-fit sm:w-auto flex justify-center sm:justify-start mx-auto sm:mx-0"
          }
        >
          {method.value === PaymentType.Mint ? (
            <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap justify-center sm:justify-start">
              <div className="bg-white/80 rounded-md px-1.5 sm:px-2 py-1 flex items-center justify-center w-14 sm:w-20">
                <img src={creditCardLogo} alt="Visa" className="h-4 sm:h-6 object-contain mx-auto" />
              </div>
              <div className="bg-white/80 rounded-md px-1.5 sm:px-2 py-1 flex items-center justify-center w-14 sm:w-20">
                <img src={masterCardLogo} alt="MasterCard" className="h-4 sm:h-6 object-contain mx-auto" />
              </div>
              <div className="bg-white/80 rounded-md px-1.5 sm:px-2 py-1 flex items-center justify-center w-14 sm:w-20">
                <img src={amexLogo} alt="American Express" className="h-4 sm:h-6 object-contain mx-auto" />
              </div>
            </div>
            ) : method.value === PaymentType.PayTo  ? (
            <div className="h-8 w-16 flex items-center justify-center mx-auto sm:mx-0">
              <svg aria-hidden="true" width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="h-8 w-8">
                <path fill="#231F20" d="M0 0h32v32H0z"></path>
                <path d="M16.286 23.385c.75.301 1.554.45 2.362.44a6.05 6.05 0 0 0 6.104-6.104 6.11 6.11 0 0 0-.561-2.556h-2.34c.5.76.772 1.646.784 2.556a3.99 3.99 0 1 1-7.973 0c0-2.133 1.764-4.039 3.898-4.039h6.967v1.936l3.547-2.95-3.54-2.946v1.896h-6.886a6.103 6.103 0 0 0-2.362 11.767Z" fill="#fff"></path>
                <path d="M11.602 21.429a5.201 5.201 0 0 1-1.432.276l.002-.003a1.726 1.726 0 0 1-1.713-1.715v-6.305h3.602a7.793 7.793 0 0 1 2.05-2.064H8.459V9H6.388v2.618H4v2.064h2.385v6.308a3.815 3.815 0 0 0 3.785 3.837 4.164 4.164 0 0 0 1.432-.3v-2.098Z" fill="#fff"></path>
              </svg>
            </div>

            ) : method.value === PaymentType.Klarna  ? (
            <div className="h-8 w-16 flex items-center justify-center mx-auto sm:mx-0">
              <svg className="sc-e8500097-0 iYIDDF" width="91" height="22" viewBox="0 0 91 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M82.2782 17.9579C80.1693 17.9579 78.5256 16.23 78.5256 14.1318C78.5256 12.0337 80.1693 10.3057 82.2782 10.3057C84.3871 10.3057 86.0308 12.0337 86.0308 14.1318C86.0308 16.23 84.3871 17.9579 82.2782 17.9579ZM81.2238 22C83.0225 22 85.3176 21.3212 86.5891 18.6676L86.7131 18.7293C86.1549 20.1795 86.1549 21.0435 86.1549 21.2595V21.5989H90.6828V6.6648H86.1549V7.00421C86.1549 7.2202 86.1549 8.08415 86.7131 9.53436L86.5891 9.59607C85.3176 6.9425 83.0225 6.26367 81.2238 6.26367C76.9129 6.26367 73.8736 9.65778 73.8736 14.1318C73.8736 18.6059 76.9129 22 81.2238 22ZM65.9963 6.26367C63.9494 6.26367 62.3367 6.97335 61.0341 9.59607L60.9101 9.53436C61.4683 8.08415 61.4683 7.2202 61.4683 7.00421V6.6648H56.9404V21.5989H61.5924V13.7307C61.5924 11.6634 62.8019 10.3675 64.7557 10.3675C66.7096 10.3675 67.671 11.4783 67.671 13.6999V21.5989H72.323V12.0954C72.323 8.70126 69.6558 6.26367 65.9963 6.26367ZM50.2105 9.59607L50.0864 9.53436C50.6447 8.08415 50.6447 7.2202 50.6447 7.00421V6.6648H46.1168V21.5989H50.7687L50.7998 14.4095C50.7998 12.3114 51.9162 11.0463 53.746 11.0463C54.2422 11.0463 54.6454 11.108 55.1106 11.2314V6.6648C53.0637 6.23282 51.2339 7.00421 50.2105 9.59607ZM35.4172 17.9579C33.3083 17.9579 31.6646 16.23 31.6646 14.1318C31.6646 12.0337 33.3083 10.3057 35.4172 10.3057C37.5261 10.3057 39.1698 12.0337 39.1698 14.1318C39.1698 16.23 37.5261 17.9579 35.4172 17.9579ZM34.3627 22C36.1615 22 38.4565 21.3212 39.728 18.6676L39.8521 18.7293C39.2938 20.1795 39.2938 21.0435 39.2938 21.2595V21.5989H43.8218V6.6648H39.2938V7.00421C39.2938 7.2202 39.2938 8.08415 39.8521 9.53436L39.728 9.59607C38.4565 6.9425 36.1615 6.26367 34.3627 6.26367C30.0519 6.26367 27.0126 9.65778 27.0126 14.1318C27.0126 18.6059 30.0519 22 34.3627 22ZM20.5308 21.5989H25.1828V0H20.5308V21.5989ZM17.1193 0H12.3743C12.3743 3.85694 9.98628 7.31276 6.35772 9.78121L4.93111 10.7686V0H0V21.5989H4.93111V10.892L13.0876 21.5989H19.1042L11.2578 11.3548C14.8243 8.79383 17.1503 4.81346 17.1193 0Z" fill="#FFA8CD"></path></svg>
            </div>
          ) : (
            <>
            <img
              src={method.icon}
              alt={method.name}
              className="h-8 max-w-16 object-contain mx-auto sm:mx-0"
            />
            </>
          )}
        </div>

        {method.value === PaymentType.Mint ? (
          <div className="flex-1 sm:pl-2">
            <span className="sr-only">{method.name}</span>
          </div>
        ) : method.value === PaymentType.PayPalPayIn4 ? (
          <div className="flex flex-col gap-1 flex-1 sm:pl-2 text-center sm:text-left">
            <span className="text-sm font-medium block select-none">{method.name}</span>
            <span
              className={cn(
                "text-xs select-none",
                isSelected ? "text-brand-blue-300/70" : ""
              )}
            >
              {method.description}
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-1 flex-1 sm:pl-2 text-center sm:text-left">
            <span className="text-sm font-medium block select-none">
              {method.name}
            </span>
            <span
              className={cn(
                "text-xs select-none",
                isSelected ? "text-brand-blue-300/70" : ""
              )}
            >
              {method.description}
            </span>
          </div>
        )}
      </label>

      {isSelected && selectedContent && <div className="pt-1">{selectedContent}</div>}
    </div>
  );
}
